import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colorTheme } from '../../../style/theme/colorTheme'
import {
	SignaturePadCanvas, SignaturePadCaption, SignaturePadClearButton, SignaturePadEraserIcon, SignaturePadFooter, SignaturePadPlaceholder, SignaturePadWrap,
} from './index.styled'

const STROKE_WIDTH = 2.5

type Props = {
	onChange: (dataUrl: string | null) => void
}

const SignaturePad = React.memo(({ onChange }: Props) => {
	const { t } = useTranslation()
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const isDrawing = useRef(false)
	const hasInk = useRef(false)
	const [isEmpty, setIsEmpty] = useState(true)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return

		const ratio = window.devicePixelRatio || 1
		const rect = canvas.getBoundingClientRect()
		canvas.width = rect.width * ratio
		canvas.height = rect.height * ratio

		const context = canvas.getContext('2d')
		if (!context) return

		context.scale(ratio, ratio)
		context.lineWidth = STROKE_WIDTH
		context.lineCap = 'round'
		context.lineJoin = 'round'
		context.strokeStyle = colorTheme.roomCharge.signature.stroke
	}, [])

	const getPosition = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
		const rect = event.currentTarget.getBoundingClientRect()
		return { x: event.clientX - rect.left, y: event.clientY - rect.top }
	}, [])

	const handlePointerDown = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
		event.preventDefault()
		const context = event.currentTarget.getContext('2d')
		if (!context) return

		isDrawing.current = true
		const { x, y } = getPosition(event)
		context.beginPath()
		context.moveTo(x, y)
	}, [getPosition])

	const handlePointerMove = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
		if (!isDrawing.current) return
		const context = event.currentTarget.getContext('2d')
		if (!context) return

		const { x, y } = getPosition(event)
		context.lineTo(x, y)
		context.stroke()

		if (!hasInk.current) {
			hasInk.current = true
			setIsEmpty(false)
		}
	}, [getPosition])

	const handlePointerUp = useCallback(() => {
		if (!isDrawing.current) return
		isDrawing.current = false
		if (hasInk.current && canvasRef.current) {
			onChange(canvasRef.current.toDataURL('image/png'))
		}
	}, [onChange])

	const handleClear = useCallback(() => {
		const canvas = canvasRef.current
		const context = canvas?.getContext('2d')
		if (!canvas || !context) return

		context.clearRect(0, 0, canvas.width, canvas.height)
		hasInk.current = false
		setIsEmpty(true)
		onChange(null)
	}, [onChange])

	return (
		<SignaturePadWrap>
			<SignaturePadCanvas ref={canvasRef}
													onPointerDown={handlePointerDown}
													onPointerMove={handlePointerMove}
													onPointerUp={handlePointerUp}
													onPointerLeave={handlePointerUp} />
			{isEmpty &&
				<SignaturePadPlaceholder>{t('RoomCharge.signature.placeholder')}</SignaturePadPlaceholder>}
			<SignaturePadFooter>
				<SignaturePadCaption>{t('RoomCharge.signature.caption')}</SignaturePadCaption>
				<SignaturePadClearButton type="button" onClick={handleClear}>
					<SignaturePadEraserIcon icon="eraser" />
					{t('RoomCharge.signature.clear')}
				</SignaturePadClearButton>
			</SignaturePadFooter>
		</SignaturePadWrap>
	)
})

SignaturePad.displayName = 'SignaturePad'
export default SignaturePad
